import { Link } from 'react-router-dom'
import type { CSSProperties, ReactNode } from 'react'
import { Alert } from '@/atoms/alert'
import { Badge } from '@/atoms/badge'
import { Button, buttonVariants } from '@/atoms/button'
import { Card, CardTitle } from '@/atoms/card'
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/atoms/dialog'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from '@/atoms/empty'
import { Field, FieldLabel, Hint } from '@/atoms/field'
import { InlineLink } from '@/atoms/inline-link'
import { Input } from '@/atoms/input'
import { MemeCard } from '@/molecules/meme-card'
import { PageContainer } from '@/atoms/page-container'
import { Progress } from '@/atoms/progress'
import { Skeleton } from '@/atoms/skeleton'
import { Spinner } from '@/atoms/spinner'
import { cn } from '../lib/cn'
import type {
  DetailTierLadderModel,
  MemeDetailModel,
  MemeDetailScreenModel,
} from '../hooks/useMemeDetailScreen'
import { ConfirmDialog } from '@/molecules/confirm-dialog'
import { MemeplexPanel } from '@/organisms/memeplex-panel'
import { Icon } from '@/atoms/icon'

/**
 * Hero 410 · gap 30 · rail 668 — exactly the 1108 column the shell gives `<main>` at 1440. The
 * pair only splits once the window can afford both (≥1100); below that the rail would be narrower
 * than the card it sits beside, so the page is one column.
 */
const detailGrid = cn('grid grid-cols-1 gap-4.5', '2xl:grid-cols-(--grid-detail) 2xl:gap-8')

const rail = 'flex min-w-0 flex-col gap-4.5'

/* one DOM order for both layouts: stacked = hero → rail; split = hero left, rail right */
const heroPlacement = '2xl:col-start-1 2xl:row-start-1'
const railPlacement = '2xl:col-start-2 2xl:row-start-1'

/** The caption under a panel heading: 13/16 on ink-muted. */
const caption = 'mt-1.5 mb-0 text-sm text-muted-foreground'

const panelRow = 'mt-4 flex flex-wrap items-center gap-2.5'

/** The sources / cap-table row: a shallow well, not a bordered box. */
const personRow = cn(
  'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted px-3.5 py-2.5',
  'text-sm text-foreground *:min-w-0',
)

const rowList = 'mt-3 flex flex-col gap-2'

/** Tier line: success colour signed in, link colour on public card — one element, one swap. */
const heroTierLine = 'm-0 text-sm font-semibold'

/** Minted / owned / tags / remix — lives at the bottom of the rail so the card can lead. */
function Provenance({ detail, badge }: { detail: MemeDetailModel; badge: ReactNode }) {
  return (
    <div data-slot="detail-provenance" className="flex min-w-0 flex-col gap-2">
      {badge}
      <p className="m-0 text-base font-medium text-muted-foreground">
        {detail.provenanceMintedBy}{' '}
        <InlineLink render={<Link {...detail.creatorLinkProps} />}>{detail.creatorName}</InlineLink>
        {detail.provenanceSeparator}
        {detail.provenanceOwnedBy}{' '}
        <InlineLink render={<Link {...detail.ownerLinkProps} />}>{detail.ownerName}</InlineLink>
        {detail.tagsLabel && (
          <>
            {detail.provenanceSeparator}
            {detail.tagsLabel}
          </>
        )}
        {detail.remixLinkProps && (
          <>
            {detail.provenanceSeparator}
            <InlineLink render={<Link {...detail.remixLinkProps} />}>
              <span aria-hidden="true">
                <Icon name="dna" size={14} />
              </span>{' '}
              {detail.provenanceRemix}
            </InlineLink>
          </>
        )}
        {detail.sourceLinkProps && (
          <>
            {detail.provenanceSeparator}
            <InlineLink {...detail.sourceLinkProps}>{detail.sourceLabel}</InlineLink>
          </>
        )}
        {detail.holdingsLabel && (
          <>
            {detail.provenanceSeparator}
            {detail.provenanceYouHold} {detail.holdingsLabel}
          </>
        )}
      </p>
    </div>
  )
}

/** Where this card sits on the rarity ladder, and the tier's own line of hype under it. */
function TierLadder({ model, hype }: { model: DetailTierLadderModel; hype: string }) {
  return (
    /* +12px top margin: MemeCard footer already gaps 6px; design wants 18 before the meter */
    <div data-slot="tier-progression" className="mt-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-semibold text-success-foreground">{model.currentLabel}</span>
        <span className="text-xs font-medium text-muted-foreground">
          {model.nextLabel}
        </span>
      </div>
      <Progress className="mt-2" value={model.value} variant="ladder" {...model.meterProps} />
      <p className={caption}>{hype}</p>
    </div>
  )
}

/** Meme detail as a function of its engine-provided model. */
export function MemeDetailScreen({
  showNotFound,
  showLoading,
  notFound,
  loadingLabel,
  detail,
}: MemeDetailScreenModel) {
  if (showNotFound)
    return (
      <PageContainer as="main" id="main" tabIndex={-1}>
        <Empty className="mt-15">
          <EmptyHeader>
            <EmptyTitle render={<h2 />}>{notFound.title}</EmptyTitle>
            <EmptyDescription>{notFound.message}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Link className={buttonVariants()} {...notFound.linkProps}>
              {notFound.linkLabel}
            </Link>
          </EmptyContent>
        </Empty>
      </PageContainer>
    )
  if (showLoading || !detail)
    return (
      <PageContainer as="main" id="main" tabIndex={-1}>
        {/* a labelled spinner row, never a bare spinner, over the shape the page is about to take */}
        <div
          data-slot="loading-state"
          className="flex items-center justify-center gap-2.5 px-5 py-10 text-sm text-muted-foreground"
          role="status"
        >
          <Spinner />
          {loadingLabel}
        </div>
        <div aria-hidden="true" className={cn('mt-5', detailGrid)}>
          <Skeleton className="aspect-square" />
          <div className={rail}>
            <Skeleton className="h-22" />
            <Skeleton className="h-41.5" />
            <Skeleton className="h-60" />
          </div>
        </div>
      </PageContainer>
    )

  /* share-link arrival: no session — the hero card title is the H1 */
  const isPublic = !!detail.signedOut
  const privateBadge = detail.private ? (
    <Badge variant="info">
      <span aria-hidden="true">
        <Icon name="eye-off" size={14} />
      </span>{' '}
      {detail.privateBadgeLabel}
    </Badge>
  ) : null

  return (
    <PageContainer as="main" id="main" tabIndex={-1}>
      <div className="mt-5">
        {/* block flow: empty live regions collapse, so they cost no space above the card */}
        <div {...detail.noticeProps}>
          {detail.notice && (
            <Alert variant="success" role="none" className="mb-3">
              {detail.notice}
            </Alert>
          )}
        </div>
        <div {...detail.errorProps}>
          {detail.error && (
            <Alert variant="error" role="none" className="mb-3">
              {detail.error}
            </Alert>
          )}
        </div>
        <div className={detailGrid}>
          <div data-slot="detail-hero" className={cn('self-start', heroPlacement)}>
            {/* MemeCard lg with tier line + meter in footer so they sit inside the card padding.
                Its frame is the meme's own ratio, so until that is measured the hero is the
                loading skeleton it replaces — it never appears at one size and jumps to another. */}
            {detail.card.aspect === null ? (
              <Skeleton data-slot="detail-hero-waiting" className="aspect-square" />
            ) : (
              <Dialog>
                <MemeCard
                  model={detail.card}
                  size="lg"
                  titleAs="h1"
                  enlargeLabel={detail.enlargeLabel}
                  subTitle={
                    <p
                      data-slot="detail-tier-line"
                      className={cn(
                        heroTierLine,
                        isPublic ? 'text-link' : 'text-success-foreground',
                      )}
                    >
                      {detail.tierLine}
                    </p>
                  }
                  footer={<TierLadder model={detail.tierLadder} hype={detail.tierHype} />}
                />
                <DialogContent size="media" showCloseButton={false}>
                  {/* one line, and no wider than the meme: `meme-fit` counts on both */}
                  <DialogHeader className="w-0 min-w-full shrink-0">
                    <DialogTitle>
                      <span className="block truncate">{detail.title}</span>
                    </DialogTitle>
                  </DialogHeader>
                  {/* the meme whole, at its own ratio, as large as the viewport allows; the
                      dialog hugs it, so nothing frames it but the dialog's own padding */}
                  <div
                    data-slot="detail-enlarged-media"
                    style={{ '--meme-aspect': detail.card.aspect } as CSSProperties}
                  >
                    {detail.card.media.kind === 'video' ? (
                      <video
                        className="block meme-fit object-cover"
                        src={detail.card.media.videoProps.src}
                        poster={detail.card.media.videoProps.poster}
                        aria-label={detail.title}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        className="block meme-fit object-cover"
                        src={detail.card.media.imageProps.src}
                        alt={detail.title}
                      />
                    )}
                  </div>
                  <DialogClose
                    aria-label={detail.closeViewerLabel}
                    className="absolute inset-0 size-full cursor-default"
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div data-slot="detail-rail" className={cn(rail, railPlacement)}>
            {detail.signedOut && (
              <Card>
                <CardTitle size="card-title">{detail.signedOut.title}</CardTitle>
                <p className={caption}>{detail.signedOut.body}</p>
                <div className={panelRow}>
                  {/* the single primary on a public card */}
                  <Button
                    variant="primary"
                    className="max-sm:w-full"
                    {...detail.signedOut.loginButtonProps}
                  >
                    {detail.signedOut.loginLabel}
                  </Button>
                  <Link
                    className={cn(buttonVariants(), 'max-sm:w-full')}
                    {...detail.signedOut.browseLinkProps}
                  >
                    {detail.signedOut.browseLabel}
                  </Link>
                </div>
                {detail.signedOut.error && (
                  <Alert variant="error" className="mt-3" {...detail.signedOut.errorProps}>
                    {detail.signedOut.error}
                  </Alert>
                )}
              </Card>
            )}

            <Card>
              <CardTitle size="title" render={<h2 />}>
                {detail.shareTitle}
              </CardTitle>
              <p className={caption}>{detail.shareCaption}</p>
              <div className={panelRow}>
                <Input
                  className="min-w-50 flex-1 max-sm:w-full max-sm:flex-none"
                  {...detail.shareInputProps}
                />
                {/* the brand companion: the card's one primary belongs to the buy control */}
                <Button variant="brand" className="max-sm:flex-1" {...detail.copyButtonProps}>
                  {detail.copyDone ? (
                    <>
                      <span aria-hidden="true">
                        <Icon name="circle-check" size={16} />
                      </span>{' '}
                    </>
                  ) : null}
                  {detail.copyButtonLabel}
                </Button>
                <a className={cn(buttonVariants(), 'max-sm:flex-1')} {...detail.previewLinkProps}>
                  {detail.previewLabel}
                </a>
              </div>
            </Card>

            {detail.listing && !detail.signedOut ? (
              <Card>
                <CardTitle size="card-title">{detail.listing.saleLabel}</CardTitle>
                {detail.listing.showBuy && detail.listing.balanceLabel && (
                  <p className={caption}>{detail.listing.balanceLabel}</p>
                )}
                {detail.listing.showBuy && (
                  <>
                    <div className={cn(panelRow, 'items-end')}>
                      <Field>
                        <FieldLabel>{detail.listing.buyLabel}</FieldLabel>
                        <Input type="number" className="w-23" {...detail.listing.buyInputProps} />
                      </Field>
                      <Button variant="primary" {...detail.listing.buyButtonProps}>
                        {detail.listing.buyButtonLabel}
                      </Button>
                    </div>
                    {detail.listing.disabledReason && <Hint>{detail.listing.disabledReason}</Hint>}
                  </>
                )}
                {detail.listing.showUnlist && (
                  <div className={panelRow}>
                    <Button {...detail.listing.unlistButtonProps}>
                      {detail.listing.unlistButtonLabel}
                    </Button>
                  </div>
                )}
              </Card>
            ) : detail.list.show ? (
              <Card>
                <CardTitle render={<h3 />} className="mb-1.5">
                  {detail.list.panelTitle}
                </CardTitle>
                <p className={caption}>{detail.list.panelCaption}</p>
                <div className={cn(panelRow, 'items-end')}>
                  <Field>
                    <FieldLabel>{detail.list.sharesFieldLabel}</FieldLabel>
                    <Input type="number" className="w-23" {...detail.list.sharesInputProps} />
                  </Field>
                  <Field>
                    <FieldLabel>
                      <span className="inline-flex items-center gap-0.5">
                        <span aria-hidden="true">
                          <Icon name="brain" size={14} />
                        </span>{' '}
                        <span aria-hidden="true">{detail.list.perShareLabel}</span>
                      </span>
                      <span className="sr-only">{detail.list.priceSrLabel}</span>
                    </FieldLabel>
                    <Input type="number" className="w-25" {...detail.list.priceInputProps} />
                  </Field>
                  <Button variant="primary" {...detail.list.listButtonProps}>
                    {detail.list.listButtonLabel}
                  </Button>
                </div>
                {detail.list.disabledReason && <Hint>{detail.list.disabledReason}</Hint>}
              </Card>
            ) : null}

            {detail.actions.length > 0 && (
              <Card>
                <CardTitle render={<h3 />} className="mb-1.5">
                  {detail.controlsTitle}
                </CardTitle>
                <p className={caption}>{detail.controlsCaption}</p>
                <div className={panelRow}>
                  {detail.actions.map((action) => (
                    <Button
                      key={action.label}
                      variant={action.variant}
                      className={cn(action.variant === 'destructive' && 'ml-auto')}
                      {...action.buttonProps}
                    >
                      <span aria-hidden="true">
                        <Icon name={action.icon} size={16} />
                      </span>{' '}
                      {action.label}
                    </Button>
                  ))}
                </div>
              </Card>
            )}

            {/* spread sources and cap table side by side under the market card */}
            <div
              data-slot="detail-spread"
              className="flex flex-wrap items-start gap-4.5 *:min-w-70 *:flex-1 [&>[data-slot=card]]:mt-0"
            >
              {detail.sources.length > 0 && (
                <Card>
                  <CardTitle render={<h3 />} className="mb-1.5">
                    <span aria-hidden="true">
                      <Icon name="satellite" size={16} />
                    </span>{' '}
                    {detail.spreadingTitle}
                  </CardTitle>
                  <div className={rowList}>
                    {detail.sources.map((source) => (
                      <div key={source.id} className={personRow}>
                        <span className="truncate">
                          {source.linkProps ? (
                            <InlineLink {...source.linkProps}>{source.label}</InlineLink>
                          ) : (
                            source.label
                          )}
                        </span>
                        <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-muted-foreground">
                          <span aria-hidden="true">
                            <Icon name="eye" size={14} />
                          </span>{' '}
                          {source.viewsLabel}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              <MemeplexPanel model={detail.plex} />
            </div>

            <Card>
              <CardTitle render={<h3 />} className="mb-1.5">
                {detail.capTableTitle}
              </CardTitle>
              <div className={rowList}>
                {detail.capTable.map((holder) => (
                  <div key={holder.userId} className={personRow}>
                    <span className="truncate font-semibold">{holder.label}</span>
                    <span className="ml-auto shrink-0">{holder.sharesLabel}</span>
                  </div>
                ))}
              </div>
              {detail.capTableNote && <p className={caption}>{detail.capTableNote}</p>}
            </Card>

            <Provenance detail={detail} badge={privateBadge} />
          </div>
        </div>
      </div>
      <ConfirmDialog model={detail.deleteDialog} />
      <ConfirmDialog model={detail.buyDialog} />
      <ConfirmDialog model={detail.claimDialog} />
    </PageContainer>
  )
}
